@if (showError) {
  <div
    class="alert alert-danger"
    role="alert"
    aria-live="assertive"
    aria-atomic="true"
    [attr.aria-label]="'Error notification'"
  >
    <div
      class="error-title"
      role="heading"
      aria-level="2"
      [attr.aria-label]="'Error title'"
    >
      {{ errorTitle | translate }}
    </div>
    <div
      role="contentinfo"
      [attr.aria-label]="'Error details'"
    >
      {{ errorMessage | translate }}
    </div>
  </div>
}

@if (showGhostElement) {
  <div
    class="skeleton-box-card mt-4"
    role="status"
    aria-live="polite"
    [attr.aria-label]="'Loading form fields'"
  >
    <div class="row">
      @for (dependenciesGhostElement of ghostElements; track $index) {
        <div class="col-12">
          <div
            class="d-flex stats-card shadow"
            aria-hidden="true"
            role="presentation"
          >
            <div
              class="skeleton-animation w-100 d-flex justify-content-center flex-column gap-3"
              aria-hidden="true"
            >
              <div class="bar bar-1"></div>
              <div class="bar bar-2"></div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}

@if (showPersonalDataForm) {
  <form
    #personalForm="ngForm"
    class="needs-validation"
    novalidate=""
    role="form"
    [attr.aria-label]="'Personal Information Form'"
    [attr.aria-describedby]="'form-instructions'"
  >
    <div
      id="form-instructions"
      class="visually-hidden"
      role="status"
    >
      Please fill in your details to proceed with the booking. Required fields are marked with an asterisk (*).
    </div>
    <div
      class="personal-form"
      role="region"
      [attr.aria-label]="'Personal Information'"
    >
      <div
        class="mb-1 cbw-personal-form-title title"
        role="heading"
        aria-level="1"
        [attr.aria-label]="'Form title'"
      >
        <app-csw-text
          [text]="( (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.PERSONAL_DATA_CARD_TITLE, type: 'template' }) | translation: 'template_content': lang )"
          customClass="step-element-title fw-600"
        />
      </div>
      <div
        class="mb-2 cbw-personal-form-title description"
        role="doc-subtitle"
        [attr.aria-label]="'Form instructions'"
      >
        <app-csw-text
          [text]="( (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.PERSONAL_DATA_CARD_SUBTITLE, type: 'template' }) | translation: 'template_content': lang )"
          [customClass]="'step-element-title'"
        />
      </div>
      @for (fieldType of fieldTypes; track fieldType) {
        @switch (fieldType.type) {
          @case ('salutation') {
            <div
              class="mb-1 cbw-personal-form cbw-personal-form-salutation"
              role="group"
              [attr.aria-labelledby]="'salutation-label'"
              [attr.aria-required]="customerRequiredFields?.indexOf('salutation') > -1"
            >
              <label
                class="mb-1"
                id="salutation-label"
                for="salutation"
                [attr.aria-required]="customerRequiredFields?.indexOf('salutation') > -1"
              >
                {{ 'form_fields.salutation' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('salutation') > -1)} }}
              </label>
              @if (widgetService.widgetConf?.context?.saluation_display_type === 'radio') {
                <div
                  ngModelGroup="salutationGroup"
                  #salutationGroup="ngModelGroup"
                  [required]="customerRequiredFields?.indexOf('salutation') > -1"
                  [ngClass]="{ 'is-invalid': salutationGroup?.touched && salutationGroup?.invalid }"
                  role="radiogroup"
                  [attr.aria-required]="customerRequiredFields?.indexOf('salutation') > -1"
                  [attr.aria-invalid]="salutationGroup?.touched && salutationGroup?.invalid"
                  [attr.aria-describedby]="salutationGroup?.touched && salutationGroup?.invalid ? 'salutation-error' : null"
                >
                  @for (salutationType of salutationTypes; track $index) {
                    <div
                      class="d-flex align-items-center mt-1 radio-item salutation-{{ salutationType.value }}"
                      role="presentation"
                    >
                      <input
                        [id]="'salutation_' + salutationType.value"
                        name="salutation"
                        type="radio"
                        [value]="salutationType.value"
                        [required]="customerRequiredFields?.indexOf('salutation') > -1"
                        [disabled]="isCustomerFieldReadonly('salutation')"
                        [(ngModel)]="userInfo.gender"
                        [attr.aria-label]="'form_fields.' + salutationType.display | translate"
                        [attr.aria-describedby]="'salutation_' + salutationType.value + '_desc'"
                        [attr.aria-required]="customerRequiredFields?.indexOf('salutation') > -1"
                      >
                      <label
                        class="radio-button-label"
                        [for]="'salutation_' + salutationType.value"
                        [id]="'salutation_' + salutationType.value + '_desc'"
                      >
                        <span>{{ 'form_fields.' + salutationType.display | translate }}</span>
                      </label>
                    </div>
                  }
                </div>
                @if (salutationGroup?.touched && salutationGroup?.invalid) {
                  <div
                    class="invalid-feedback"
                    id="salutation-error"
                    role="alert"
                    aria-live="polite"
                    [attr.aria-label]="'Salutation error'"
                  >
                    @if (salutationGroup?.invalid) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.salutation' | translate }) }}
                    }
                  </div>
                }
              } @else {
                <ng-select
                  id="salutation"
                  name="gender"
                  [(ngModel)]="userInfo.gender"
                  #salutation="ngModel"
                  [required]="customerRequiredFields?.indexOf('salutation') > -1"
                  [disabled]="isCustomerFieldReadonly('salutation')"
                  [clearable]="false"
                  [ngClass]="{ 'is-invalid': salutation.touched && salutation.invalid }"
                  [attr.aria-required]="customerRequiredFields?.indexOf('salutation') > -1"
                  [attr.aria-invalid]="salutation.touched && salutation.invalid"
                  [attr.aria-label]="'form_fields.salutation' | translate"
                  [attr.aria-describedby]="salutation.touched && salutation.invalid ? 'salutation-select-error' : null"
                >
                  @for (salutationType of salutationTypes; track salutationType) {
                    <ng-option
                      [value]="salutationType.value"
                      [attr.aria-label]="'form_fields.' + salutationType.display | translate"
                    >
                      {{ 'form_fields.' + salutationType.display | translate }}
                    </ng-option>
                  }
                </ng-select>
                @if (salutation.touched && salutation.invalid) {
                  <div
                    class="invalid-feedback"
                    id="salutation-select-error"
                    role="alert"
                    aria-live="polite"
                    [attr.aria-label]="'Salutation selection error'"
                  >
                    @if (salutation.errors?.required) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.salutation' | translate }) }}
                    }
                  </div>
                }
              }
            </div>
          }
          @case ('prename') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-prename">
              <label class="mb-1" for="firstName" id="firstName-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PRENAME_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('prename') > -1)} ) }}
              </label>
              <input
                type="text"
                class="form-control"
                id="firstName"
                [required]="customerRequiredFields?.indexOf('prename') > -1"
                [readonly]="isCustomerFieldReadonly('prename')"
                name="forename"
                [(ngModel)]="userInfo.forename"
                #forename="ngModel"
                phishingProtection
                [ngClass]="{ 'is-invalid': forename.touched && forename.invalid }"
                [attr.aria-required]="customerRequiredFields?.indexOf('prename') > -1"
                [attr.aria-invalid]="forename.touched && forename.invalid"
                aria-labelledby="firstName-label"
                autocomplete="given-name"
              >
              @if (forename.touched && forename.invalid) {
                <div class="invalid-feedback" role="alert" aria-live="polite">
                  @if (forename.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PRENAME_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (forename?.errors?.phishingLinkDetected) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang) }}
                  }
                </div>
              }
            </div>
          }
          @case ('lastname') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-lastname">
              <label class="mb-1" for="lastName" id="lastName-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_LASTNAME_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('lastname') > -1)} ) }}
              </label>
              <input
                type="text"
                class="form-control"
                id="lastName"
                [required]="customerRequiredFields?.indexOf('lastname') > -1"
                [readonly]="isCustomerFieldReadonly('lastname')"
                name="lastName"
                [(ngModel)]="userInfo.lastName"
                #lastName="ngModel"
                phishingProtection
                [ngClass]="{ 'is-invalid': lastName.touched && lastName.invalid }"
                [attr.aria-required]="customerRequiredFields?.indexOf('lastname') > -1"
                [attr.aria-invalid]="lastName.touched && lastName.invalid"
                aria-labelledby="lastName-label"
                autocomplete="family-name"
              >
              @if (lastName.touched && lastName.invalid) {
                <div class="invalid-feedback" role="alert" aria-live="polite">
                  @if (lastName.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_LASTNAME_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (lastName?.errors?.phishingLinkDetected) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.ERROR_MESSAGE_PHISHING_LINKS_INPUT, type: 'template' }) | translation: 'template_content': lang) }}
                  }
                </div>
              }
            </div>
          }
          @case ('birthday') {
            <div class="mb-2 cbw-personal-form cbw-personal-form-birthday csw-date-field" role="group" aria-labelledby="birthday-label">
              <label class="mb-1" id="birthday-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_BIRTHDAY_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang) | addRequired: {isRequired: (customerRequiredFields?.indexOf('birthday') > -1)} }}
              </label>

              <mat-form-field>
                <input
                  name="birthday"
                  matInput
                  [matDatepicker]="myPicker"
                  [(ngModel)]='userInfo.birthday'
                  [max]="maximumPossibleDateForDatePicker"
                  #birthday="ngModel"
                  [required]="customerRequiredFields?.indexOf('birthday') > -1"
                  [disabled]="isCustomerFieldReadonly('birthday')"
                  [attr.aria-required]="customerRequiredFields?.indexOf('birthday') > -1"
                  [attr.aria-invalid]="birthday.touched && birthday.invalid"
                  aria-labelledby="birthday-label"
                  autocomplete="bday"
                  [attr.aria-describedby]="birthday.touched && birthday.invalid ? 'birthday-error' : null"
                >
                <mat-datepicker-toggle matSuffix [for]="myPicker" aria-label="Select your date of birth" />
                <mat-datepicker #myPicker aria-label="Date of birth picker" />
              </mat-form-field>
              @if (birthday.touched && birthday.invalid) {
                <div class="text-danger" id="birthday-error" role="alert" aria-live="polite">
                  @if (birthday.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_BIRTHDAY_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else {
                    {{ 'error_messages.invalid_error_message' | translate }}
                  }
                </div>
              }
            </div>
          }
          @case ('meeting_types') {
            @if (rawMeetingTypes?.length > 1 && showMeetingTypes) {
              <div class="mb-1 cbw-personal-form cbw-personal-form-meeting_types" role="group" aria-labelledby="meeting-type-label">
                <label class="input-label" id="meeting-type-label"
                    [innerHTML]="(((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_WHERE_SHOULD_THE_MEETING_TAKE_PLACE, type: 'template' }) | translation: 'template_content': lang ) | addRequired: {isRequired: (customerRequiredFields?.indexOf('meeting_types') > -1)}) | calioSafe: 'html'"
                >
                  {{ 'form_fields.meeting_type' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('meeting_types') > -1)} }}
                </label>
                <ng-select
                  name="meeting_type_id"
                  [(ngModel)]="userInfo.meeting_type_id"
                  #meeting_type_id="ngModel"
                  [items]="rawMeetingTypes"
                  bindValue="id"
                  [clearable]="false"
                  [required]="customerRequiredFields?.indexOf('meeting_types') > -1"
                  [disabled]="isCustomerFieldReadonly('meeting_types')"
                  #meetingTypesModel="ngModel"
                  [ngClass]="{ 'is-invalid': meetingTypesModel.touched && meetingTypesModel.invalid }"
                  (change)="onMeetingTypeChange($event)"
                  [attr.aria-required]="customerRequiredFields?.indexOf('meeting_types') > -1"
                  [attr.aria-invalid]="meetingTypesModel.touched && meetingTypesModel.invalid"
                  aria-labelledby="meeting-type-label"
                  [attr.aria-describedby]="meetingTypesModel.touched && meetingTypesModel.invalid ? 'meeting-type-error' : null"
                  role="combobox"
                  aria-haspopup="listbox"
                >
                  <ng-template ng-option-tmp let-item="item">
                    <div class="d-flex align-items-center" role="option" [attr.aria-label]="(bookingService.widgetTemplates | calioMeetingTemplate: { id: item.id, type: 'meeting_template' })?.titleTemplate | translation: 'template_content': lang">
                      <div class="meeting-icon d-flex align-items-center" aria-hidden="true" [attr.aria-label]="'Meeting type icon for ' + ((bookingService.widgetTemplates | calioMeetingTemplate: { id: item.id, type: 'meeting_template' })?.titleTemplate | translation: 'template_content': lang)">
                        @if (item.id === meetingTypeId.STORE) {
                          <img src="{{ imagePath }}/images/meetings/store.svg" width="26px" alt="Store" />
                        } @else if (item.id === meetingTypeId.ZOOM) {
                          <img src="{{ imagePath }}/images/meetings/zoom.svg" width="26px" alt="Zoom" />
                        } @else if (item.id === meetingTypeId.PHONE) {
                          <img src="{{ imagePath }}/images/meetings/phone.svg" width="26px" height="30px" alt="Phone" />
                        } @else if (item.id === meetingTypeId.CUSTOMER) {
                          <img src="{{ imagePath }}/images/meetings/customer.svg" width="26px" alt="Customer" />
                        } @else if (item.id === meetingTypeId.UNBLU) {
                          <img src="{{ imagePath }}/images/meetings/unblu.svg" width="26px" alt="Unblu" />
                        } @else if (item.id === meetingTypeId.MICROSOFT_TEAMS) {
                          <img src="{{ imagePath }}/images/meetings/teams.svg" width="26px" alt="Teams" />
                        } @else if (item.id === meetingTypeId.GOOGLE_MEET) {
                          <img src="{{ imagePath }}/images/meetings/meet.svg" width="26px" alt="Google Meet" />
                        } @else if (item.id === meetingTypeId.GOTO_MEETING) {
                          <img src="{{ imagePath }}/images/meetings/gotomeeting.png" width="26px" alt="GoToMeeting" />
                        } @else if (item.id === meetingTypeId.WEBEX) {
                          <img src="{{ imagePath }}/images/meetings/webex.png" width="26px" alt="Cisco Webex" />
                        } @else if (item.id === meetingTypeId.WILL_CALL_YOU) {
                          <img src="{{ imagePath }}/images/meetings/phone.svg" width="26px" height="30px" alt="Phone" />
                        } @else if (item.id === meetingTypeId.METAVERSE) {
                          <img src="{{ imagePath }}/images/meetings/metaverse.svg" width="26px" height="30px" alt="Metaverse" />
                        } @else if (item.id === meetingTypeId.KMEET) {
                          <img src="{{ imagePath }}/images/meetings/kmeet.png" width="26px" height="30px" alt="kMeet" />
                        }
                      </div>
                      @if ((bookingService.widgetTemplates | calioMeetingTemplate: { id: item.id, type: 'meeting_template' }); as widgetTemplate) {
                        <div class="meeting-option">
                          <div
                            class="meeting-type-name"
                            [innerHTML]="(widgetTemplate.titleTemplate | translation: 'template_content': lang) | calioSafe: 'html'"
                            role="heading"
                            aria-level="3"
                          ></div>
                          <div
                            class="meeting-type-desc"
                            [innerHTML]="(widgetTemplate.descTemplate | translation: 'template_content': lang) | calioSafe: 'html'"
                            role="doc-subtitle"
                          ></div>
                          @if (item.id === 1 && selectedStore) {
                            <div class="cbw-summary-worker-store-location" role="complementary" aria-label="Store location">
                              <span class="cbw-summary-worker-store-location-name">{{ selectedStore | translation: 'name' : lang }}</span>
                              @if (selectedStore.is_online === 0) {
                                <span class="cbw-summary-worker-store-location-separator" aria-hidden="true">,&nbsp;</span>
                                <span class="cbw-summary-worker-store-location-address whitespace-pre-line">
                                  @let addressInput = {
                                    street: selectedStore.street,
                                    zip: selectedStore.zip,
                                    city: selectedStore.city,
                                    country: selectedStore.country | translation: 'name' : lang,
                                    country_code: selectedStore.country?.identifier,
                                  };
                                  {{ addressInput | addressFormat: 'single' }}
                                </span>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </ng-template>
                  <ng-template ng-label-tmp let-item="item">
                    @if ((bookingService.widgetTemplates | calioMeetingTemplate: { id: item.id, type: 'meeting_template' }); as widgetTemplate) {
                      <span
                        [innerHTML]="(widgetTemplate.titleTemplate | translation: 'template_content': lang) | calioSafe: 'html'"
                        [attr.aria-label]="(widgetTemplate.titleTemplate | translation: 'template_content': lang) + ' - ' + (widgetTemplate.descTemplate | translation: 'template_content': lang)"
                      ></span>
                    }
                  </ng-template>
                </ng-select>
                @if (meetingTypesModel.touched && meetingTypesModel.invalid) {
                  <div class="invalid-feedback" id="meeting-type-error" role="alert" aria-live="polite">
                    @if (meetingTypesModel.errors?.required) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_WHERE_SHOULD_THE_MEETING_TAKE_PLACE, type: 'template' }) | translation: 'template_content': lang }) }}
                    } @else if (meetingTypesModel.errors?.minlength) {
                      {{ 'error_messages.minimum_length_error_message' | translate: {'char': 4} }}
                    } @else if (meetingTypesModel.errors?.maxlength) {
                      {{ 'error_messages.max_char_error_message' | translate: {'char': 6} }}
                    }
                  </div>
                }
              </div>
            }
          }
          @case ('notification_preferences') {
            @if (widgetService?.widgetConf?.context?.ask_for_customer_notification_preference && NOTIFICATION_TYPES?.length) {
              <div class="mb-1 cbw-personal-form cbw-personal-form-notification_preferences" role="group" aria-labelledby="notification-type-label">
                <label class="input-label" id="notification-type-label">
                  {{ 'form_fields.notification_type' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('notification_preferences') > -1)} }}
                </label>
                <ng-select
                  name="customer_notification_preference"
                  [(ngModel)]="userInfo.customer_notification_preference"
                  [clearable]="false"
                  [required]="customerRequiredFields?.indexOf('notification_preferences') > -1"
                  [disabled]="isCustomerFieldReadonly('notification_preferences')"
                  [ngClass]="{ 'is-invalid': customer_notification_preference.touched && customer_notification_preference.invalid }"
                  #customer_notification_preference="ngModel"
                  [attr.aria-required]="customerRequiredFields?.indexOf('notification_preferences') > -1"
                  [attr.aria-invalid]="customer_notification_preference.touched && customer_notification_preference.invalid"
                  aria-labelledby="notification-type-label"
                  [attr.aria-describedby]="customer_notification_preference.touched && customer_notification_preference.invalid ? 'notification-type-error' : null"
                >
                  @for (notificationType of NOTIFICATION_TYPES; track notificationType) {
                    <ng-option [value]="notificationType.value" [attr.aria-label]="'form_fields.' + notificationType.display | translate">
                      {{ 'form_fields.' + notificationType.display | translate }}
                    </ng-option>
                  }
                </ng-select>
                @if (customer_notification_preference.touched && customer_notification_preference.invalid) {
                  <div class="invalid-feedback" id="notification-type-error" role="alert" aria-live="polite">
                    @if (customer_notification_preference.errors?.required) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.notification_type' | translate }) }}
                    } @else if (customer_notification_preference.errors?.minlength) {
                      {{ 'error_messages.minimum_length_error_message' | translate: {'char': 4} }}
                    } @else if (customer_notification_preference.errors?.maxlength) {
                      {{ 'error_messages.max_char_error_message' | translate: {'char': 6} }}
                    }
                  </div>
                }
              </div>
            }
          }
          @case ('email') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-email">
              <label class="mb-1" for="email" id="email-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_EMAIL_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('email') > -1)} ) }}
              </label>
              <input
                type="email"
                class="form-control"
                id="email"
                name="eMail"
                [(ngModel)]="userInfo.eMail"
                #eMail="ngModel"
                [pattern]="emailRegex"
                [ngClass]="{ 'is-invalid': eMail.touched && eMail.invalid }"
                [required]="customerRequiredFields?.indexOf('email') > -1"
                [readonly]="isCustomerFieldReadonly('email')"
                [placeholder]="((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_EMAIL_PLACEHOLDER, type: 'template' }) | translation: 'template_content': lang)"
                [attr.aria-required]="customerRequiredFields?.indexOf('email') > -1"
                [attr.aria-invalid]="eMail.touched && eMail.invalid"
                aria-labelledby="email-label"
                [attr.aria-describedby]="eMail.touched && eMail.invalid ? 'email-error' : null"
                autocomplete="email"
              >
              @if (eMail.touched && eMail.invalid) {
                <div class="invalid-feedback" id="email-error" role="alert" aria-live="polite">
                  @if (eMail.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_WHERE_SHOULD_THE_MEETING_TAKE_PLACE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (eMail.errors?.minlength) {
                    {{ 'error_messages.minimum_length_error_message' | translate: {'char': 4} }}
                  } @else if (eMail.errors?.maxlength) {
                    {{ 'error_messages.max_char_error_message' | translate: {'char': 6} }}
                  } @else if (eMail.errors?.pattern) {
                    {{ 'error_messages.email_error_message' | translate }}
                  }
                </div>
              }
            </div>
          }
          @case ('phone') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-phone" role="group" aria-labelledby="phone-label">
              <label class="mb-1" id="phone-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PHONENUMBER_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('phone') > -1)} ) }}
              </label>
              <app-calio-tel-input
                name="phone"
                [(ngModel)]="userInfo.phone"
                [defaultCountry]="(partnerData?.country?.identifier)"
                #phone="ngModel"
                [cssClass]="((phone.touched && phone.invalid) ? 'is-invalid': '')"
                [required]="customerRequiredFields?.indexOf('phone') > -1"
                [isDisabled]="isCustomerFieldReadonly('phone')"
                [attr.aria-required]="customerRequiredFields?.indexOf('phone') > -1"
                [attr.aria-invalid]="phone.touched && phone.invalid"
                aria-labelledby="phone-label"
                [attr.aria-describedby]="phone.touched && phone.invalid ? 'phone-error' : null"
                autocomplete="tel"
              />
              @if (phone.touched && phone.invalid) {
                <div class="error-message" id="phone-error" role="alert" aria-live="polite">
                  @if (phone.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PHONENUMBER_LABEL_TEMPLATE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (phone.errors?.invalidNumber) {
                    {{ 'error_messages.invalid_phone_number' | translate }}
                  }
                </div>
              }
            </div>
          }
          @case ('mobile') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-mobile" role="group" aria-labelledby="mobile-label">
              <label class="mb-1" id="mobile-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.SHOW_MOBILE_ON_WIDGET, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('mobile') > -1)} ) }}
              </label>
              <app-calio-tel-input
                name="mobile"
                [(ngModel)]="userInfo.mobile"
                [defaultCountry]="(partnerData?.country?.identifier)"
                #mobile="ngModel"
                [cssClass]="((mobile.touched && mobile.invalid) ? 'is-invalid': '')"
                [required]="customerRequiredFields?.indexOf('mobile') > -1"
                [isDisabled]="isCustomerFieldReadonly('mobile')"
                [attr.aria-required]="customerRequiredFields?.indexOf('mobile') > -1"
                [attr.aria-invalid]="mobile.touched && mobile.invalid"
                aria-labelledby="mobile-label"
                [attr.aria-describedby]="mobile.touched && mobile.invalid ? 'mobile-error' : null"
                autocomplete="tel-mobile"
              />
              @if (mobile.touched && mobile.invalid) {
                <div class="error-message" id="mobile-error" role="alert" aria-live="polite">
                  @if (mobile.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.SHOW_MOBILE_ON_WIDGET, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (mobile.errors?.invalidNumber) {
                    {{ 'error_messages.invalid_phone_number' | translate }}
                  }
                </div>
              }
            </div>
          }
          @case ('address') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-street" role="group" aria-labelledby="street-label">
              <label class="mb-1" for="street" id="street-label">{{ 'form_fields.street' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('address') > -1)} }}</label>
              <input
                type="text"
                class="form-control"
                id="street"
                name="street"
                [readonly]="isCustomerFieldReadonly('address', 'street')"
                [(ngModel)]="userInfo.street"
                #street="ngModel"
                phishingProtection
                [ngClass]="{ 'is-invalid': street.touched && street.invalid }"
                [required]="customerRequiredFields?.indexOf('address') > -1"
                [attr.aria-required]="customerRequiredFields?.indexOf('address') > -1"
                [attr.aria-invalid]="street.touched && street.invalid"
                aria-labelledby="street-label"
                [attr.aria-describedby]="street.touched && street.invalid ? 'street-error' : null"
                autocomplete="street-address"
              >
              @if (street.touched && street.invalid) {
                <div class="invalid-feedback" id="street-error" role="alert" aria-live="polite">
                  @if (street.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.street' | translate }) }}
                  } @else if (street.errors?.minlength) {
                    {{ 'error_messages.minimum_length_error_message' | translate: {'char': 4} }}
                  } @else if (street.errors?.maxlength) {
                    {{ 'error_messages.max_char_error_message' | translate: {'char': 6} }}
                  } @else if (street?.errors?.phishingLinkDetected) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.ERROR_MESSAGE_PHISHING_LINKS_INPUT, type: 'template' }) | translation: 'template_content': lang) }}
                  }
                </div>
              }
            </div>
            <div class="row mb-1 cbw-personal-form cbw-personal-form-zip" role="group" aria-labelledby="zip-city-label">
              <div class="col-4">
                <label class="mb-1" for="zip" id="zip-label">
                  {{ 'form_fields.zip' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('address') > -1)} }}
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="zip"
                  [required]="customerRequiredFields?.indexOf('address') > -1"
                  [readonly]="isCustomerFieldReadonly('address', 'zip')"
                  name="zip"
                  [(ngModel)]="userInfo.zip"
                  #zip="ngModel"
                  [pattern]="zipRegex"
                  [ngClass]="{ 'is-invalid': zip.touched && zip.invalid, 'cbw-zip-prefilled': isZipPrefilled() }"
                  [attr.aria-required]="customerRequiredFields?.indexOf('address') > -1"
                  [attr.aria-invalid]="zip.touched && zip.invalid"
                  aria-labelledby="zip-label"
                  [attr.aria-describedby]="zip.touched && zip.invalid ? 'zip-error' : null"
                  autocomplete="postal-code"
                  (input)="onZipInput(userInfo.zip)"
                  (blur)="onZipBlur()"
                  (change)="onZipSuggestionSelected($event)"
                  [attr.list]="widgetService?.widgetConf?.partner?.is_zip_codes_search_enabled === 1 ? 'zipList' : null"
                >
                @if (widgetService?.widgetConf?.partner?.is_zip_codes_search_enabled === 1 && citySuggestions?.length) {
                  <datalist id="zipList">
                    @for (city of citySuggestions; track city.zip + city.city18) {
                      <option [value]="city.zip + ' (' + city.city18 + ')'"></option>
                    }
                  </datalist>
                }
                @if (zip.touched && zip.invalid) {
                  <div class="invalid-feedback" id="zip-error" role="alert" aria-live="polite">
                    @if (zip.errors?.required) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.zip' | translate }) }}
                    } @else if (zip.errors?.pattern) {
                      {{ 'error_messages.invalid_zip' | translate }}
                    }
                  </div>
                }
              </div>
              <div class="col-8">
                <label class="mb-1" for="city" id="city-label">
                  {{ 'form_fields.city' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('address') > -1)} }}
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="city"
                  [required]="customerRequiredFields?.indexOf('address') > -1"
                  [readonly]="isCustomerFieldReadonly('address', 'city')"
                  name="city"
                  [(ngModel)]="userInfo.city"
                  #city="ngModel"
                  phishingProtection
                  [ngClass]="{ 'is-invalid': city.touched && city.invalid }"
                  [attr.aria-required]="customerRequiredFields?.indexOf('address') > -1"
                  [attr.aria-invalid]="city.touched && city.invalid"
                  aria-labelledby="city-label"
                  [attr.aria-describedby]="city.touched && city.invalid ? 'city-error' : null"
                  autocomplete="address-level2"
                >
                @if (city.touched && city.invalid) {
                  <div class="invalid-feedback" id="city-error" role="alert" aria-live="polite">
                    @if (city.errors?.required) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'form_fields.city' | translate }) }}
                    } @else if (city.errors?.minlength) {
                      {{ 'error_messages.minimum_length_error_message' | translate: {'char': 4} }}
                    } @else if (city.errors?.maxlength) {
                      {{ 'error_messages.max_char_error_message' | translate: {'char': 6} }}
                    } @else if (city?.errors?.phishingLinkDetected) {
                      {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.ERROR_MESSAGE_PHISHING_LINKS_INPUT, type: 'template' }) | translation: 'template_content': lang) }}
                    }
                  </div>
                }
              </div>
            </div>
          }
          @case ('comment') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-comment" role="group" aria-labelledby="comment-label">
              <label class="mb-1" for="comment" id="comment-label">
                {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_MESSAGE_BOOKING_QUESTION_TABLE_TEMPLATE, type: 'template' }) | translation: 'template_content': lang | addRequired: {isRequired: (customerRequiredFields?.indexOf('comment') > -1)} )}}
              </label>
              <textarea
                class="form-control"
                id="comment"
                [required]="customerRequiredFields?.indexOf('comment') > -1"
                [readonly]="isCustomerFieldReadonly('comment')"
                name="comment"
                [(ngModel)]="userInfo.comment"
                #comment="ngModel"
                phishingProtection
                [ngClass]="{ 'is-invalid': comment.touched && comment.invalid }"
                cols="30"
                rows="5"
                [attr.aria-required]="customerRequiredFields?.indexOf('comment') > -1"
                [attr.aria-invalid]="comment.touched && comment.invalid"
                aria-labelledby="comment-label comment-helper"
                [attr.aria-describedby]="comment.touched && comment.invalid ? 'comment-error' : null"
              ></textarea>
              <small class="calio-text-muted" id="comment-helper">
                {{ 'form_fields.comment_helper_text' | translate }}
              </small>
              @if (comment.touched && comment.invalid) {
                <div class="invalid-feedback" id="comment-error" role="alert" aria-live="polite">
                  @if (comment.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: (bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_MESSAGE_BOOKING_QUESTION_TABLE_TEMPLATE, type: 'template' }) | translation: 'template_content': lang }) }}
                  } @else if (comment?.errors?.phishingLinkDetected) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.ERROR_MESSAGE_PHISHING_LINKS_INPUT, type: 'template' }) | translation: 'template_content': lang) }}
                  }
                </div>
              }
            </div>
          }
          @case ('title') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-title" role="group" aria-labelledby="title-label">
              <label for="title" id="title-label">
                {{ 'personal_form.title' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('title') > -1)}  }}
              </label>
              <ng-select
                id="title"
                name="title"
                [(ngModel)]="userInfo.title"
                #title="ngModel"
                [required]="customerRequiredFields?.indexOf('title') > -1"
                [disabled]="isCustomerFieldReadonly('title')"
                [clearable]="false"
                [ngClass]="{ 'is-invalid': title.touched && title.invalid }"
                [attr.aria-required]="customerRequiredFields?.indexOf('title') > -1"
                [attr.aria-invalid]="title.touched && title.invalid"
                aria-labelledby="title-label"
                [attr.aria-describedby]="title.touched && title.invalid ? 'title-error' : null"
              >
                @for (title of titleTypes; track title) {
                  <ng-option [value]="title.id" [attr.aria-label]="title.name | translate">
                    {{ title.name | translate }}
                  </ng-option>
                }
              </ng-select>
              @if (title.touched && title.invalid) {
                <div class="invalid-feedback" id="title-error" role="alert" aria-live="polite">
                  @if (title.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'personal_form.title' | translate }) }}
                  }
                </div>
              }
            </div>
          }
          @case ('company') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-company" role="group" aria-labelledby="company-label">
              <label class="mb-1" for="company" id="company-label">
                {{ 'personal_form.company' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('company') > -1)}  }}
              </label>
              <input
                type="text"
                class="form-control"
                id="company"
                [required]="customerRequiredFields?.indexOf('company') > -1"
                [readonly]="isCustomerFieldReadonly('company')"
                name="company"
                [(ngModel)]="userInfo.company"
                phishingProtection
                #company="ngModel"
                [ngClass]="{ 'is-invalid': company.touched && company.invalid }"
                [attr.aria-required]="customerRequiredFields?.indexOf('company') > -1"
                [attr.aria-invalid]="company.touched && company.invalid"
                aria-labelledby="company-label"
                [attr.aria-describedby]="company.touched && company.invalid ? 'company-error' : null"
                autocomplete="organization"
              >
              @if (company.touched && company.invalid) {
                <div class="invalid-feedback" id="company-error" role="alert" aria-live="polite">
                  @if (company.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'personal_form.company' | translate }) }}
                  } @else if (company.errors?.phishingLinkDetected) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.ERROR_MESSAGE_PHISHING_LINKS_INPUT, type: 'template' }) | translation: 'template_content': lang) }}
                  }
                </div>
              }
            </div>
          }
          @case ('country') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-country" role="group" aria-labelledby="country-label">
              <label class="input-label" id="country-label">
                {{ 'personal_form.country' | translate | addRequired: {isRequired: (customerRequiredFields?.indexOf('country') > -1)} }}
              </label>
              <ng-select
                id="country"
                name="country"
                [(ngModel)]="userInfo.country"
                [clearable]="false"
                [required]="customerRequiredFields?.indexOf('country') > -1"
                [disabled]="isCustomerFieldReadonly('country')"
                [ngClass]="{ 'is-invalid': country.touched && country.invalid }"
                #country="ngModel"
                [attr.aria-required]="customerRequiredFields?.indexOf('country') > -1"
                [attr.aria-invalid]="country.touched && country.invalid"
                aria-labelledby="country-label"
                [attr.aria-describedby]="country.touched && country.invalid ? 'country-error' : null"
              >
                @for (option of (countries | sortTranslationList: 'name': lang); track option) {
                  <ng-option [value]="option.id" [attr.aria-label]="option | translation: 'name': lang">
                    <div>
                      <span>
                        {{ option | translation: 'name': lang }}
                      </span>
                    </div>
                  </ng-option>
                }
              </ng-select>
              @if (country.touched && country.invalid) {
                <div class="invalid-feedback" id="country-error" role="alert" aria-live="polite">
                  @if (country.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'personal_form.country' | translate }) }}
                  }
                </div>
              }
            </div>
          }
          @case ('policy') {
            <div class="mb-1 cbw-personal-form cbw-personal-form-policy" role="group" aria-labelledby="policy-label">
              <div class="custom-control custom-checkbox my-1">
                <input
                  id="policy"
                  name="policy"
                  type="checkbox"
                  class="custom-control-input"
                  [(ngModel)]="userInfo.policy"
                  #policy="ngModel"
                  [ngClass]="{ 'is-invalid': policy.touched && policy.invalid }"
                  [required]="customerRequiredFields.indexOf('policy') > -1"
                  [disabled]="isCustomerFieldReadonly('policy')"
                  [attr.aria-required]="customerRequiredFields.indexOf('policy') > -1"
                  [attr.aria-invalid]="policy.touched && policy.invalid"
                  aria-labelledby="policy-label"
                  [attr.aria-describedby]="policy.touched && policy.invalid ? 'policy-error' : null"
                >
                <label class="custom-control-label ps-2" id="policy-label">
                  <span [innerHtml]="('personal_form.accept_data_policy' | translate) | trustHtml"></span>
                  @if (customerRequiredFields?.indexOf('policy') > -1) {
                    <span aria-hidden="true">*</span>
                  }
                </label>
              </div>
              @if (policy.touched && policy.invalid) {
                <div class="text-danger" id="policy-error" role="alert" aria-live="polite">
                  @if (policy.errors?.required) {
                    {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'personal_form.accept_data_policy' | translate }) }}
                  }
                </div>
              }
            </div>
          }
          @case ('CUSTOM_FIELD') {
            @if (widgetQuestions[fieldType.value]) {
              <div class="mb-2" role="group" [attr.aria-labelledby]="'custom-field-' + fieldType.value + '-label'" [attr.aria-describedby]="'custom-field-' + fieldType.value + '-description'">
                <app-booking-question
                  [customField]="widgetQuestions[fieldType.value]"
                  [selectedCustomFieldValues]="selectedCustomFieldValues"
                  (radioButtonChangeEvent)="onRadioButtonChangeEvent($event)"
                  (workerSelectAnswerInvalidated)="onWorkerSelectAnswerInvalidated($event)"
                  [attr.id]="'custom-field-' + fieldType.value"
                  [attr.aria-label]="widgetQuestions[fieldType.value].label | translate"
                  [attr.aria-description]="widgetQuestions[fieldType.value].description | translate"
                />
                @if (widgetQuestions[fieldType.value]?.customFieldDependencies?.length) {
                  @for (dependencyCustomField of widgetQuestions[fieldType.value]?.customFieldDependencies; track dependencyCustomField;
                  let childIndex = $index; let childIsFirst = $first; let childIsLast = $last) {
                    <app-booking-question
                      [customField]="dependencyCustomField"
                      [selectedCustomFieldValues]="selectedCustomFieldValues"
                      (radioButtonChangeEvent)="onRadioButtonChangeEvent($event)"
                      (workerSelectAnswerInvalidated)="onWorkerSelectAnswerInvalidated($event)"
                      [attr.id]="'custom-field-' + dependencyCustomField.id"
                      [attr.aria-label]="dependencyCustomField.label | translate"
                      [attr.aria-description]="dependencyCustomField.description | translate"
                    />
                  }
                }
                @if (widgetQuestions?.[fieldType.value]?.showDependenciesGhostElement) {
                  <div class="skeleton-box-card" role="status" aria-label="Loading additional fields">
                    <div class="row">
                      <div class="col-12">
                        <div class="d-flex stats-card shadow">
                          <div class="skeleton-animation w-100 d-flex justify-content-center flex-column gap-3">
                            <div class="bar bar-1"></div>
                            <div class="bar bar-2"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          }
        }
      }
      @if (
        bookingService.partnerData?.is_captcha_feature_enabled &&
        bookingService.partnerData?.captcha_type === captchaType.FRIENDLY
      ) {
        <div class="mb-1 cbw-personal-form cbw-personal-form-friendly-captcha" role="region" [attr.aria-label]="'Security verification'">
          <div #friendlyCaptcha class="frc-captcha"></div>
          @if (friendlyCaptchaValidationRequired) {
            <div class="text-danger" id="captcha-error" role="alert" aria-live="polite">
              {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_INPUT_DEFAULT_ERROR_VALIDATION_MESSAGE, type: 'template' }) | translation: 'template_content': lang | calioTranslateResolver: { text: 'personal_form.accept_data_policy' | translate }) }}
            </div>
          }
        </div>
      }
    </div>

    <div class="d-flex justify-content-end mt-4 cbw-custom-navigation" role="navigation" aria-label="Form navigation controls">
      @if (widgetService?.widgetConf?.type === widgetTypes.LEAD_GENERATOR) {
        <div>
          <app-button
            id="personal-form-finish-btn"
            (clickEvent)="submit(personalForm)"
            [fontColor]="widgetService?.widgetConf?.button_font_color"
            [backgroundColor]="widgetService?.widgetConf?.button_background_color"
            aria-label="Submit your information"
            role="button"
          >
            {{ 'form_fields.submit' | translate }}
          </app-button>
        </div>
      } @else {
        <div class="me-2">
          <app-button
            id="personal-form-previous-btn"
            (clickEvent)="previousQuestion()"
            [fontColor]="widgetService?.widgetConf?.button_font_color"
            [backgroundColor]="widgetService?.widgetConf?.button_background_color"
            role="button"
            [attr.aria-label]="'Go to previous step'"
          >
            <span class="custom-button-icon back" aria-hidden="true"></span>
            {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PREVIOUS_BUTTON, type: 'template' }) | translation: 'template_content': lang )}}
          </app-button>
        </div>
        <div>
          <app-button
            id="personal-form-finish-btn"
            (clickEvent)="submit(personalForm)"
            [fontColor]="widgetService?.widgetConf?.button_font_color"
            [backgroundColor]="widgetService?.widgetConf?.button_background_color"
            role="button"
            [attr.aria-label]="'Go to next step'"
          >
            {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_NEXT_BUTTON, type: 'template' }) | translation: 'template_content': lang )}}
            <span class="custom-button-icon next" aria-hidden="true"></span>
          </app-button>
        </div>
      }
    </div>
  </form>
}
